/**
 * Cities Selectors
 * Memoized selectors for accessing cities state
 */

import { createSelector } from '@spcc/core'
import type { CitiesState } from './cities.slice'
import type { CityListItem } from '../domain/types'
import { applyFilters } from '../domain/utils'

// ===========================================
// Root State Type
// ===========================================

interface RootState {
  cities: CitiesState
}

// ===========================================
// Base Selector
// ===========================================

const selectCitiesState = (state: RootState) => state.cities

// ===========================================
// Simple Selectors
// ===========================================

export const selectCities = createSelector(
  selectCitiesState,
  (state) => state.items
)

export const selectSelectedCity = createSelector(
  selectCitiesState,
  (state) => state.selectedCity
)

export const selectCityStats = createSelector(
  selectCitiesState,
  (state) => state.stats
)

export const selectCitiesFilter = createSelector(
  selectCitiesState,
  (state) => state.filter
)

export const selectCitiesPagination = createSelector(
  selectCitiesState,
  (state) => state.pagination
)

export const selectAvailableRegions = createSelector(
  selectCitiesState,
  (state) => state.availableRegions
)

export const selectAvailableCountries = createSelector(
  selectCitiesState,
  (state) => state.availableCountries
)

export const selectCitiesLoading = createSelector(
  selectCitiesState,
  (state) => state.loading
)

export const selectCityDetailLoading = createSelector(
  selectCitiesState,
  (state) => state.detailLoading
)

export const selectCitiesError = createSelector(
  selectCitiesState,
  (state) => state.error
)

// ===========================================
// Filtered Selector
// ===========================================

export const selectFilteredCities = createSelector(
  [selectCities, selectCitiesFilter],
  (items, filter) => applyFilters(items, filter)
)

// ===========================================
// Computed Selectors
// ===========================================

export const selectCitiesCount = createSelector(selectCities, (items) => items.length)

export const selectCityByDocumentId = (documentId: string) =>
  createSelector(selectCities, (items) =>
    items.find((item) => item.documentId === documentId) ?? null
  )

export const selectActiveCities = createSelector(selectCities, (items) =>
  items.filter((item) => item.isActive)
)

// ===========================================
// UI State Selectors
// ===========================================

export const selectIsCitiesEmpty = createSelector(
  [selectCities, selectCitiesLoading],
  (items, loading) => !loading && items.length === 0
)

export const selectHasCitiesError = createSelector(
  selectCitiesError,
  (error) => error !== null
)

// ===========================================
// Summary Selectors (for dropdowns)
// ===========================================

export const selectCitySummaries = createSelector(selectActiveCities, (cities) =>
  cities.map((c) => ({
    id: c.id,
    documentId: c.documentId,
    code: c.code,
    name: c.name,
    isActive: c.isActive,
  }))
)
